import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import { useDispatch, useSelector } from 'react-redux';
import {
  createLeaveRequest,
  getEmpLeaveAbsenceType,
  getLeaveDropDown,
  syncEmployeeLeaves,
} from '../../../core/data/redux/leaveSlice';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import Api from '../../../core/data/redux/utils/apiCall';
import { API_URLS } from '../../../core/data/redux/apis/urls';
import { formatEmployeeId } from '../../../utils/empIdUtils';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import LeaveSummaryModal from './LeaveSummaryModal';
import {
  leaveUsesDischargeDateForRMDAForm,
  absenceHoursToABRST,
  decimalHoursToTimeString,
  formatDateToAPI,
  formatDateDisplay,
  getTomorrowDateString,
  getAlertsForLeaveType,
  viewLeaveAttachmentFile,
} from './leaveUtils';

interface LeaveManagementCreateProps {}

interface LeaveAbsenceType {
  EmployeeID: string;
  InfoType: string;
  AbsenceTypeCode: string;
  AbsenceTypeName: string;
  ActionCreate: boolean;
  ActionDelete: boolean;
  ActionModify: boolean;
  ApproverReadOnlyInd: boolean;
  ApproverVisibleInd: boolean;
  AllowEmptyApprover: boolean;
  AllowedDurationPartialDayInd: boolean;
  AllowedDurationSingleDayInd: boolean;
  AllowedDurationMultipleDayInd: boolean;
  NoteVisibleInd: boolean;
  DefaultType: boolean;
  AttachmentEnabled: boolean;
  AttachmentMandatory: boolean;
  AttachMaxSize: number;
  AttachRestrictFileType: boolean;
  AttachSupportFileType: string;
  TimeUnitCode: string;
  TimeUnitName: string;
  ApproverName: string;
  ApproverPernr: string;
  ApproverLevel: number;
  MultipleApproverFlag: boolean;
  AddDelApprovers: boolean;
  ActionApprove: string;
  AddFieldProp: string;
  AttabsInd: string;
  AdditionalFields: {
    results: Array<{
      Fieldname: string;
      ProcState: string;
      Required: string;
      Readonly: string;
      FieldLabel: string;
      ShowInitValue: string;
      ShowAtReviewSentOnly: string;
      Length: string;
      Decimals: string;
      Type_Kind: string;
      Name: string;
      HasF4: string;
    }>;
  };
}

interface DropdownItem {
  id: number;
  dd: string;
  key: string;
  value: string;
}

interface UploadedDocument {
  id: number;
  name: string;
  type: string;
  uploaded: boolean;
  url: string;
  fileName: string;
  fileExtension: string;
  fileSize: string;
}

const LeaveManagementCreate: React.FC<LeaveManagementCreateProps> = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // On-behalf-of override from location state if any
  const locationState = (location.state as any) || {};
  const overrideEmployeeID = locationState.overrideEmployeeID;
  const overrideEmployeeName = locationState.overrideEmployeeName;

  const employeesState = useSelector((state: any) => state.employeesState);
  const rawEmpId = overrideEmployeeID || employeesState?.data?.data?.empId || '';
  const empId = useMemo(() => formatEmployeeId(rawEmpId), [rawEmpId]);

  const { leaveAbsenceTypes, leaveDropDown } = useSelector((state: any) => state.leavesState || {});

  // Fetch profile if empId is not available yet
  useEffect(() => {
    if (!rawEmpId) {
      dispatch(getMyProfile({}) as any);
    }
  }, [dispatch, rawEmpId]);

  // Fetch Leave Absence Types for employee
  useEffect(() => {
    if (empId) {
      dispatch(getEmpLeaveAbsenceType({ empId }) as any);
    }
  }, [dispatch, empId]);

  // Fetch Dropdown options from backend
  useEffect(() => {
    dispatch(getLeaveDropDown({}) as any);
  }, [dispatch]);

  // Extract absence types list from API response
  const absenceTypesList: LeaveAbsenceType[] = useMemo(() => {
    if (!leaveAbsenceTypes) return [];
    if (Array.isArray(leaveAbsenceTypes)) return leaveAbsenceTypes;
    if (Array.isArray(leaveAbsenceTypes?.Leave_Types)) return leaveAbsenceTypes.Leave_Types;
    if (Array.isArray(leaveAbsenceTypes?.data?.Leave_Types)) return leaveAbsenceTypes.data.Leave_Types;
    if (Array.isArray(leaveAbsenceTypes?.data?.data?.Leave_Types)) return leaveAbsenceTypes.data.data.Leave_Types;
    return [];
  }, [leaveAbsenceTypes]);

  // Categorize dropdown items into buckets
  const dropdownBuckets = useMemo(() => {
    const rawList: DropdownItem[] = Array.isArray(leaveDropDown)
      ? leaveDropDown
      : Array.isArray(leaveDropDown?.data)
      ? leaveDropDown.data
      : Array.isArray(leaveDropDown?.data?.data)
      ? leaveDropDown.data.data
      : [];

    return {
      customer02: rawList.filter((i) => i.dd === 'Customer02'), // Location of Accident
      customer09: rawList.filter((i) => i.dd === 'Customer09'), // Leave Reason
      customer10: rawList.filter((i) => i.dd === 'Customer10'), // Day Type
    };
  }, [leaveDropDown]);

  // Selected leave type config object
  const [selectedLeaveConfig, setSelectedLeaveConfig] = useState<LeaveAbsenceType | null>(null);

  // Form Data state
  const tomorrowStr = useMemo(() => getTomorrowDateString(), []);
  const [formData, setFormData] = useState({
    leaveType: '',
    startDate: tomorrowStr,
    endDate: tomorrowStr,
    dischargeDate: '',
    expectedDueDate: '',
    processor: '',
    absencehours: '0:00',
    newNote: '',
    reasonForLeave1: '',
    reasonForLeave2: '',
    cityDistrict: '',
    hospitalizationStatus: '',
    addressLine3: '',
    isSterilizationOfSelf: false,
    dayType: '',
    locationOfAccident: '',
    leaveReasonDropdown: '',
  });

  // Dynamic fields storage for any extra SAP fields
  const [dynamicFields, setDynamicFields] = useState<Record<string, string>>({});

  // Attachments state
  const attachmentIdRef = useRef(0);
  const [attachments, setAttachments] = useState<UploadedDocument[]>([]);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [selectedFileForUpload, setSelectedFileForUpload] = useState<File | null>(null);
  const [customAttachmentName, setCustomAttachmentName] = useState('');
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal & submission state
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [validationData, setValidationData] = useState<any>(null);

  // Leave Type Dropdown UI state
  const [showLeaveTypeDropdown, setShowLeaveTypeDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Auto-select default leave type when list loads
  useEffect(() => {
    const creatable = absenceTypesList.filter((lt) => lt.ActionCreate !== false);
    if (creatable.length > 0 && !selectedLeaveConfig) {
      const defaultLeave = creatable.find((lt) => lt.DefaultType) || creatable[0];
      if (defaultLeave) {
        setSelectedLeaveConfig(defaultLeave);
        setFormData((prev) => ({
          ...prev,
          leaveType: defaultLeave.AbsenceTypeName,
          processor: defaultLeave.ApproverPernr || prev.processor,
        }));
      }
    }
  }, [absenceTypesList, selectedLeaveConfig]);

  // Check if current leave type uses discharge date for RMDDA
  const useDischargeDateForRmda = useMemo(() => {
    return leaveUsesDischargeDateForRMDAForm(
      formData.leaveType,
      'WFHL',
      selectedLeaveConfig?.AbsenceTypeCode,
      selectedLeaveConfig?.AbsenceTypeName
    );
  }, [formData.leaveType, selectedLeaveConfig]);

  // Check if Maternity leave
  const isMaternityLeave = useMemo(() => {
    const name = String(formData.leaveType || '').toLowerCase();
    const code = String(selectedLeaveConfig?.AbsenceTypeCode || '').toUpperCase();
    return name.includes('maternity') || code === 'MATL';
  }, [formData.leaveType, selectedLeaveConfig]);

  // Calculate leave days between start and end date
  const calculateLeaveDays = () => {
    if (!formData.startDate || !formData.endDate) return 1;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays > 0 ? diffDays : 1;
  };

  // Handle Leave Type selection
  const handleLeaveTypeSelect = (config: LeaveAbsenceType) => {
    setSelectedLeaveConfig(config);
    setFormData((prev) => {
      const updated = {
        ...prev,
        leaveType: config.AbsenceTypeName,
        processor: config.ApproverPernr || prev.processor,
      };

      // Reset partial day fields if not allowed
      if (!config.AllowedDurationPartialDayInd) {
        updated.absencehours = '0:00';
        updated.dayType = '';
      }

      // Sync end date with start date for single-day only leaves
      if (config.AllowedDurationSingleDayInd && !config.AllowedDurationMultipleDayInd) {
        updated.endDate = prev.startDate;
      }

      return updated;
    });

    setShowLeaveTypeDropdown(false);
    setSearchQuery('');
  };

  // Generic form input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        // If single day only, keep end date identical to start date
        if (name === 'startDate' && selectedLeaveConfig?.AllowedDurationSingleDayInd && !selectedLeaveConfig?.AllowedDurationMultipleDayInd) {
          next.endDate = value;
        }
        return next;
      });
    }
  };

  // Dynamic additional field change handler
  const handleDynamicFieldChange = (fieldName: string, value: string) => {
    setDynamicFields((prev) => ({ ...prev, [fieldName]: value }));
  };

  // File picker modal open
  const handleOpenUploadModal = () => {
    setSelectedFileForUpload(null);
    setCustomAttachmentName('');
    setShowAttachmentModal(true);
  };

  // Handle file chosen in upload modal
  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSizeBytes = (selectedLeaveConfig?.AttachMaxSize || 5) * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error(`File size exceeds maximum allowed ${selectedLeaveConfig?.AttachMaxSize || 5}MB.`);
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const allowed = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
    if (!allowed.includes(ext)) {
      toast.error('Unsupported file format. Please upload PDF, JPG, PNG, or DOC.');
      return;
    }

    setSelectedFileForUpload(file);
    if (!customAttachmentName) {
      setCustomAttachmentName(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  // Upload file to AWS bucket
  const handleUploadAttachmentFromModal = async () => {
    if (!selectedFileForUpload) {
      toast.error('Please choose a file to upload first.');
      return;
    }

    setIsUploadingFile(true);
    try {
      // Read file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(selectedFileForUpload);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (err) => reject(err);
      });

      // Extract pure base64 without data URL prefix (matches mobile RNFS.readFile)
      const base64String = base64.includes(',') ? base64.split(',')[1] : base64;

      const uploadPayload = {
        filestring: base64String,
        mime_type: selectedFileForUpload.type || 'application/pdf',
        module: 'LeaveManagement',
      };

      const res: any = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, uploadPayload);

      // Extract S3 full URL or key, matching mobile LeaveApplyForm (line 497: res.data.fileUrl || res.data.url || res.data.key)
      const fileKeyOrUrl =
        res?.data?.data?.fileUrl ||
        res?.data?.data?.url ||
        res?.data?.fileUrl ||
        res?.data?.url ||
        res?.fileUrl ||
        res?.url ||
        res?.data?.data?.key ||
        res?.data?.key ||
        res?.key;

      const fileExt =
        res?.data?.data?.fileExtension ||
        res?.data?.fileExtension ||
        res?.fileExtension ||
        selectedFileForUpload.name.split('.').pop()?.toLowerCase() ||
        'pdf';

      // Ensure raw bytes are captured for SAP NUMC12 field
      const rawBytes =
        selectedFileForUpload.size ||
        parseInt(String(res?.data?.data?.fileSize || res?.data?.fileSize || res?.fileSize || '').replace(/[^\d]/g, ''), 10) ||
        0;
      const fileSize = rawBytes > 0 ? String(rawBytes) : '0';

      if (fileKeyOrUrl) {
        attachmentIdRef.current += 1;
        const displayName = customAttachmentName.trim() || selectedFileForUpload.name.replace(/\.[^/.]+$/, '');
        const fullName = `${displayName}.${fileExt}`;

        const newDoc: UploadedDocument = {
          id: attachmentIdRef.current,
          name: displayName,
          type: fileExt,
          uploaded: true,
          url: fileKeyOrUrl,
          fileName: fullName,
          fileExtension: fileExt,
          fileSize: String(fileSize),
        };

        setAttachments((prev) => [...prev, newDoc]);
        setShowAttachmentModal(false);
        setSelectedFileForUpload(null);
        setCustomAttachmentName('');
        toast.success(res?.data?.message || res?.message || 'Attachment uploaded successfully!');
      } else {
        toast.error(res?.data?.message || res?.message || 'Failed to get uploaded file URL. Please try again.');
      }
    } catch (err: any) {
      console.error('File upload error:', err);
      toast.error(err?.message || 'Failed to upload attachment.');
    } finally {
      setIsUploadingFile(false);
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (id: number) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
    toast.info('Attachment removed.');
  };

  // Build the complete API payload mirroring mobile buildCreateLeavePayload exactly
  const buildCreateLeavePayload = (processCheckOnlyInd: boolean, actionCode: number = 1) => {
    const absenceTypeCode = selectedLeaveConfig?.AbsenceTypeCode ?? 'CASL';
    const absenceTypeNameForApi = (selectedLeaveConfig?.AbsenceTypeName ?? 'Casual Leave').replace(/\s+/g, '');

    const additionalFields: Record<string, string | null> = {
      RMDDA: null,
      ABRST: absenceHoursToABRST(formData.absencehours),
      CUSTOMER01: formData.reasonForLeave1 || '',
      CUSTOMER02: dropdownBuckets.customer02.find((i) => i.value === formData.locationOfAccident)?.key || '',
      CUSTOMER03: formData.reasonForLeave2 || '',
      CUSTOMER04: '',
      CUSTOMER05: formData.cityDistrict || '',
      CUSTOMER06: formData.hospitalizationStatus || '',
      CUSTOMER07: formData.addressLine3 || '',
      CUSTOMER08: formData.isSterilizationOfSelf ? 'X' : '',
      CUSTOMER09: dropdownBuckets.customer09.find((i) => i.value === formData.leaveReasonDropdown)?.key || '',
      CUSTOMER10: dropdownBuckets.customer10.find((i) => i.value === formData.dayType)?.key || '',
    };

    // Add any extra dynamic fields from AdditionalFields results
    Object.keys(dynamicFields).forEach((key) => {
      if (additionalFields[key] === undefined) {
        additionalFields[key] = dynamicFields[key] || '';
      }
    });

    if (isMaternityLeave && formData.expectedDueDate) {
      additionalFields.RMDDA = formatDateToAPI(formData.expectedDueDate);
    }
    if (useDischargeDateForRmda && formData.dischargeDate) {
      additionalFields.RMDDA = formatDateToAPI(formData.dischargeDate);
    }

    const notes = formData.newNote?.trim() || formData.reasonForLeave1 || '';

    return {
      AdditionalFields: additionalFields,
      EmployeeID: empId,
      ChangeStateID: 1,
      InfoType: '2001',
      ActionCode: actionCode,
      ProcessCheckOnlyInd: processCheckOnlyInd,
      AbsenceTypeCode: absenceTypeCode,
      AbsenceTypeName: absenceTypeNameForApi,
      StartDate: formatDateToAPI(formData.startDate),
      EndDate: formatDateToAPI(formData.endDate),
      Notes: notes,
      AttachmentsExist: attachments.length > 0 ? 'X' : '',
      AttachmentDetails: attachments.map((doc) => {
        const numericBytes = String(doc.fileSize ?? '').replace(/[^\d]/g, '') || '0';
        return {
          url: doc.url,
          fileName: doc.fileName,
          fileExtension: doc.fileExtension,
          fileSize: numericBytes.padStart(12, '0'),
        };
      }),
    };
  };

  // Perform form validation before Check or Send
  const validateForm = () => {
    if (!formData.leaveType) {
      toast.error('Please select a leave type.');
      return false;
    }
    if (!formData.startDate) {
      toast.error('Please select start date.');
      return false;
    }
    if (!formData.endDate) {
      toast.error('Please select end date.');
      return false;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error('End Date cannot be before Start Date.');
      return false;
    }

    // Attachment mandatory validation
    if (selectedLeaveConfig?.AttachmentMandatory && attachments.length === 0) {
      toast.error('Attachment is mandatory for this leave type.');
      return false;
    }

    // Check additional fields required flags
    const addFields = selectedLeaveConfig?.AdditionalFields?.results || [];
    for (const f of addFields) {
      if (f.Required === 'X') {
        let val = '';
        if (f.Fieldname === 'RMDDA') {
          val = useDischargeDateForRmda ? formData.dischargeDate : formData.expectedDueDate;
        } else if (f.Fieldname === 'CUSTOMER02') val = formData.locationOfAccident;
        else if (f.Fieldname === 'CUSTOMER09') val = formData.leaveReasonDropdown;
        else if (f.Fieldname === 'CUSTOMER10') val = formData.dayType;
        else if (f.Fieldname === 'CUSTOMER01') val = formData.reasonForLeave1;
        else if (f.Fieldname === 'CUSTOMER03') val = formData.reasonForLeave2;
        else if (f.Fieldname === 'CUSTOMER05') val = formData.cityDistrict;
        else if (f.Fieldname === 'CUSTOMER06') val = formData.hospitalizationStatus;
        else if (f.Fieldname === 'CUSTOMER07') val = formData.addressLine3;
        else val = dynamicFields[f.Fieldname] || '';

        if (!val) {
          toast.error(`Please fill out required field: ${f.FieldLabel || f.Fieldname}`);
          return false;
        }
      }
    }

    return true;
  };

  // Action: Check (Validate only)
  const handleCheck = async () => {
    if (!validateForm()) return;
    setIsChecking(true);

    try {
      const payload = buildCreateLeavePayload(true, 1);
      const res: any = await dispatch(createLeaveRequest(payload) as any).unwrap();

      // Check if SAP recalculated working hours duration
      let sapWorkingHoursStr: string | undefined =
        res?.data?.data?.WorkingHoursDuration ||
        res?.data?.WorkingHoursDuration ||
        res?.WorkingHoursDuration ||
        res?.raw?.data?.data?.WorkingHoursDuration;

      let recalculated = false;
      if (sapWorkingHoursStr !== undefined && sapWorkingHoursStr !== null) {
        const sapHoursNum = parseFloat(String(sapWorkingHoursStr));
        if (!isNaN(sapHoursNum)) {
          const parts = (formData.absencehours || '0:00').split(':');
          const formHours = Math.floor(Number(parts[0]) || 0);
          const formMins = Math.floor(Number(parts[1]) || 0);
          const formDecimal = formHours + formMins / 60;

          if (Math.abs(sapHoursNum - formDecimal) > 0.01) {
            recalculated = true;
            const newAbsenceHours = decimalHoursToTimeString(sapHoursNum);
            setFormData((prev) => ({ ...prev, absencehours: newAbsenceHours }));
            toast.warning(`Working hours have been recalculated by SAP to ${newAbsenceHours} hrs.`);
          }
        }
      }

      if (!recalculated) {
        toast.success('Validation successful! Leave request is ready for submission.');
      }
    } catch (err: any) {
      console.error('Check validation error:', err);
      const msg = typeof err === 'string' ? err : err?.message || 'Validation failed. Please check your inputs.';
      toast.error(msg);
    } finally {
      setIsChecking(false);
    }
  };

  // Action: Send (Validate, then open Leave Summary Modal)
  const handleSend = async () => {
    if (!validateForm()) return;
    setIsChecking(true);

    try {
      const payload = buildCreateLeavePayload(true, 1);
      const res: any = await dispatch(createLeaveRequest(payload) as any).unwrap();

      const valData = res?.data?.data || res?.data || res?.raw?.data?.data || res;
      setValidationData(valData);
      setShowSummaryModal(true);
    } catch (err: any) {
      console.error('Validation before send error:', err);
      const msg = typeof err === 'string' ? err : err?.message || 'Validation failed. Please check your inputs.';
      toast.error(msg);
    } finally {
      setIsChecking(false);
    }
  };

  const toSyncYmd = (value: string) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const match = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
  };

  // Create writes to SAP first. The list API stays empty until that day is synced.
  const syncCreatedLeave = async () => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const days = Array.from(new Set([
      today,
      toSyncYmd(formData.startDate),
      toSyncYmd(formData.endDate),
    ].filter(Boolean)));

    for (const day of days) {
      await dispatch(syncEmployeeLeaves({
        empId,
        fromDate: day,
        toDate: day,
      }) as any).unwrap();
    }
    return days;
  };

  // Action: Confirm Leave Submission from LeaveSummaryModal
  const handleConfirmLeave = async () => {
    setShowSummaryModal(false);
    setIsSubmitting(true);

    try {
      const payload = buildCreateLeavePayload(false, 1);
      await dispatch(createLeaveRequest(payload) as any).unwrap();

      let syncDates: string[] = [];
      try {
        syncDates = await syncCreatedLeave();
      } catch (syncErr) {
        console.error('Post-create leave sync failed:', syncErr);
      }

      Swal.fire({
        icon: 'success',
        title: 'Leave Request Sent!',
        html: `
          <div class="text-start">
            <p class="mb-2"><strong>${formData.leaveType}</strong> leave request has been sent for approval!</p>
          </div>
        `,
        confirmButtonColor: '#A93226',
        confirmButtonText: 'View Applications',
        allowOutsideClick: false,
      }).then(() => {
        navigate(routes.LeaveManagement.list, {
          state: {
            ...(overrideEmployeeID ? locationState : {}),
            justCreated: true,
            syncDates,
          },
        });
      });
    } catch (err: any) {
      console.error('Final leave submission failed:', err);
      const msg = typeof err === 'string' ? err : err?.message || 'Failed to submit leave request. Please try again.';
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: msg,
        confirmButtonColor: '#A93226',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filtered leave types for search dropdown
  const creatableLeaveTypes = useMemo(
    () => absenceTypesList.filter((type) => type.ActionCreate !== false),
    [absenceTypesList]
  );

  const filteredLeaveTypes = useMemo(() => {
    if (!searchQuery) return creatableLeaveTypes;
    return creatableLeaveTypes.filter((type) =>
      type.AbsenceTypeName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [creatableLeaveTypes, searchQuery]);

  const titleText = overrideEmployeeName
    ? `Leave Request: New, On Behalf of ${overrideEmployeeName}`
    : 'Apply New Leave';

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header & Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.LeaveManagement.list}
                state={overrideEmployeeID ? locationState : undefined}
                className="btn btn-outline-secondary btn-sm rounded-circle me-3 d-flex align-items-center justify-content-center"
                style={{ width: '36px', height: '36px' }}
                title="Back to List"
              >
                <i className="ti ti-arrow-left fs-18"></i>
              </Link>
              <div>
                <h3 className="page-title mb-0 fw-bold text-dark">{titleText}</h3>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-0 py-1" style={{ fontSize: '13px' }}>
                    <li className="breadcrumb-item">
                      <Link to="/" className="text-secondary">Home</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={routes.LeaveManagement.list} state={overrideEmployeeID ? locationState : undefined} className="text-secondary">Leave Management</Link>
                    </li>
                    <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                      Apply Leave
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-light text-dark border px-3 py-2 fs-13 d-flex align-items-center">
              <i className="ti ti-user me-1 text-primary"></i>
              Employee ID: <strong>{empId || 'Loading...'}</strong>
            </span>
          </div>
        </div>

        {/* Main Form Content */}
        <div className="row">
          <div className="col-lg-12">
            {/* Section 1: Type of Leave */}
            <div className="card shadow-sm border-0 mb-4 rounded-3">
              <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <h5 className="card-title fw-bold text-dark mb-0 d-flex align-items-center">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center me-2 text-white"
                    style={{ backgroundColor: '#A93226', width: '28px', height: '28px', fontSize: '14px' }}
                  >
                    1
                  </span>
                  Type of Leave <span className="text-danger ms-1">*</span>
                </h5>
                {selectedLeaveConfig && (
                  <div className="d-flex gap-2">
                    {selectedLeaveConfig.AllowedDurationSingleDayInd && !selectedLeaveConfig.AllowedDurationMultipleDayInd && (
                      <span className="badge bg-warning-subtle text-warning border px-2 py-1 fs-12">Single Day Only</span>
                    )}
                    {selectedLeaveConfig.AllowedDurationPartialDayInd && (
                      <span className="badge bg-info-subtle text-info border px-2 py-1 fs-12">Partial Day Allowed</span>
                    )}
                    {selectedLeaveConfig.AttachmentMandatory && (
                      <span className="badge bg-danger-subtle text-danger border px-2 py-1 fs-12">Attachment Mandatory</span>
                    )}
                  </div>
                )}
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-md-8 col-lg-6 position-relative">
                    <label className="form-label text-dark fw-semibold">
                      Select Leave Absence Type <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control bg-light cursor-pointer"
                        value={formData.leaveType || 'Loading leave types...'}
                        readOnly
                        onClick={() => setShowLeaveTypeDropdown(!showLeaveTypeDropdown)}
                        style={{ cursor: 'pointer' }}
                        placeholder="Select Leave Type"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setShowLeaveTypeDropdown(!showLeaveTypeDropdown)}
                      >
                        <i className={`ti ti-chevron-${showLeaveTypeDropdown ? 'up' : 'down'}`}></i>
                      </button>
                    </div>

                    {/* Dropdown Menu */}
                    {showLeaveTypeDropdown && (
                      <div
                        className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
                        style={{ zIndex: 1050, maxHeight: '320px', overflowY: 'auto' }}
                      >
                        <div className="p-2 border-bottom sticky-top bg-white">
                          <div className="input-group input-group-sm">
                            <span className="input-group-text bg-light border-end-0">
                              <i className="ti ti-search text-muted"></i>
                            </span>
                            <input
                              type="text"
                              className="form-control bg-light border-start-0"
                              placeholder="Search leave type..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              autoFocus
                            />
                          </div>
                        </div>
                        {filteredLeaveTypes.length > 0 ? (
                          filteredLeaveTypes.map((type, idx) => {
                            const isSelected = formData.leaveType === type.AbsenceTypeName;
                            return (
                              <div
                                key={idx}
                                className={`p-3 border-bottom d-flex align-items-center justify-content-between ${
                                  isSelected ? 'bg-primary-subtle text-primary fw-bold' : 'text-dark'
                                }`}
                                style={{ cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                                onClick={() => handleLeaveTypeSelect(type)}
                                onMouseEnter={(e) => {
                                  if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) e.currentTarget.style.backgroundColor = 'white';
                                }}
                              >
                                <div>
                                  <div className="fs-14">{type.AbsenceTypeName}</div>
                                  <div className="text-muted fw-normal" style={{ fontSize: '11px' }}>
                                    Code: {type.AbsenceTypeCode} {type.DefaultType ? '• Default' : ''}
                                  </div>
                                </div>
                                {isSelected && <i className="ti ti-check text-primary fs-18"></i>}
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-3 text-center text-muted fs-13">
                            No matching leave types found.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: General Data */}
            <div className="card shadow-sm border-0 mb-4 rounded-3">
              <div className="card-header bg-white py-3 border-bottom">
                <h5 className="card-title fw-bold text-dark mb-0 d-flex align-items-center">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center me-2 text-white"
                    style={{ backgroundColor: '#A93226', width: '28px', height: '28px', fontSize: '14px' }}
                  >
                    2
                  </span>
                  General Data
                </h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  {/* Start Date */}
                  <div className="col-md-6 col-lg-4">
                    <label className="form-label text-dark fw-semibold">
                      Start Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* End Date */}
                  <div className="col-md-6 col-lg-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <label className="form-label text-dark fw-semibold">
                        End Date <span className="text-danger">*</span>
                      </label>
                      <span className="badge bg-light text-secondary border fs-12 mb-1">
                        {calculateLeaveDays()} {calculateLeaveDays() === 1 ? 'Day' : 'Days'}
                      </span>
                    </div>
                    <input
                      type="date"
                      className="form-control"
                      name="endDate"
                      value={
                        selectedLeaveConfig?.AllowedDurationSingleDayInd &&
                        !selectedLeaveConfig?.AllowedDurationMultipleDayInd
                          ? formData.startDate
                          : formData.endDate
                      }
                      onChange={handleInputChange}
                      min={formData.startDate}
                      disabled={
                        selectedLeaveConfig?.AllowedDurationSingleDayInd &&
                        !selectedLeaveConfig?.AllowedDurationMultipleDayInd
                      }
                      required
                    />
                  </div>

                  {/* Processor — same authorization as the app (ApproverVisibleInd / ApproverReadOnlyInd) */}
                  {selectedLeaveConfig?.ApproverVisibleInd && (
                    <div className="col-md-6 col-lg-4">
                      <label className="form-label text-dark fw-semibold">
                        Processor :
                        {selectedLeaveConfig?.ApproverReadOnlyInd && (
                          <span className="badge bg-light text-muted ms-1 fs-11">Readonly</span>
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="processor"
                        value={
                          selectedLeaveConfig?.ApproverReadOnlyInd
                            ? selectedLeaveConfig.ApproverPernr || formData.processor
                            : formData.processor
                        }
                        onChange={handleInputChange}
                        readOnly={selectedLeaveConfig?.ApproverReadOnlyInd}
                        placeholder="Select"
                      />
                      <div className="form-text">
                        {selectedLeaveConfig.ApproverName
                          ? `Approver: ${selectedLeaveConfig.ApproverName}`
                          : 'Approver is assigned from your leave authorization'}
                        {selectedLeaveConfig.ApproverLevel != null
                          ? ` · Level ${selectedLeaveConfig.ApproverLevel}`
                          : ''}
                      </div>
                    </div>
                  )}

                  {/* Absence Hours (partial day only) */}
                  {selectedLeaveConfig?.AllowedDurationPartialDayInd && (
                    <div className="col-md-6 col-lg-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <label className="form-label text-dark fw-semibold">
                          Absence Hours (H:MM)
                        </label>
                        <div className="btn-group btn-group-sm mb-1" role="group">
                          <button
                            type="button"
                            className="btn btn-outline-secondary py-0 px-2 fs-11"
                            onClick={() => setFormData((p) => ({ ...p, absencehours: '4:00' }))}
                          >
                            Half (4:00)
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary py-0 px-2 fs-11"
                            onClick={() => setFormData((p) => ({ ...p, absencehours: '8:00' }))}
                          >
                            Full (8:00)
                          </button>
                        </div>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        name="absencehours"
                        value={formData.absencehours}
                        onChange={handleInputChange}
                        placeholder="e.g. 4:00 or 8:00"
                      />
                    </div>
                  )}

                  {/* New Note / Reason */}
                  <div className="col-12">
                    <label className="form-label text-dark fw-semibold">
                      Note / Description
                    </label>
                    <textarea
                      className="form-control"
                      name="newNote"
                      value={formData.newNote}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Enter details or reason for leave request..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Additional Data (Dynamic from SAP config) */}
            {selectedLeaveConfig?.AdditionalFields?.results &&
              selectedLeaveConfig.AdditionalFields.results.length > 0 && (
                <div className="card shadow-sm border-0 mb-4 rounded-3 animate__animated animate__fadeIn">
                  <div className="card-header bg-white py-3 border-bottom">
                    <h5 className="card-title fw-bold text-dark mb-0 d-flex align-items-center">
                      <span
                        className="rounded-circle d-flex align-items-center justify-content-center me-2 text-white"
                        style={{ backgroundColor: '#A93226', width: '28px', height: '28px', fontSize: '14px' }}
                      >
                        3
                      </span>
                      Additional Data
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {selectedLeaveConfig.AdditionalFields.results.map((field, index) => {
                        if (!field.FieldLabel) return null;
                        if (field.Fieldname === 'ABRST') return null; // Handled in general data

                        const isRequired = field.Required === 'X';
                        const isReadonly = field.Readonly === 'X';
                        const fieldName = field.Fieldname;

                        // RMDDA (Discharge date vs Expected due date)
                        if (fieldName === 'RMDDA') {
                          const label = useDischargeDateForRmda
                            ? 'Discharge Date'
                            : isMaternityLeave
                            ? 'Expected Due Date'
                            : field.FieldLabel;

                          const val = useDischargeDateForRmda
                            ? formData.dischargeDate
                            : formData.expectedDueDate;

                          return (
                            <div className="col-md-6 col-lg-4" key={index}>
                              <label className="form-label text-dark fw-semibold">
                                {label} {isRequired && <span className="text-danger">*</span>}
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                name={useDischargeDateForRmda ? 'dischargeDate' : 'expectedDueDate'}
                                value={val}
                                onChange={handleInputChange}
                                required={isRequired}
                                disabled={isReadonly}
                              />
                            </div>
                          );
                        }

                        // CUSTOMER02: Location of Accident dropdown
                        if (fieldName === 'CUSTOMER02') {
                          return (
                            <div className="col-md-6 col-lg-4" key={index}>
                              <label className="form-label text-dark fw-semibold">
                                {field.FieldLabel} {isRequired && <span className="text-danger">*</span>}
                              </label>
                              <select
                                className="form-select"
                                name="locationOfAccident"
                                value={formData.locationOfAccident}
                                onChange={handleInputChange}
                                required={isRequired}
                                disabled={isReadonly}
                              >
                                <option value="">Select Location</option>
                                {dropdownBuckets.customer02.map((item) => (
                                  <option key={item.id || item.key} value={item.value}>
                                    {item.value}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        }

                        // CUSTOMER09: Reason for Leave dropdown
                        if (fieldName === 'CUSTOMER09') {
                          return (
                            <div className="col-md-6 col-lg-4" key={index}>
                              <label className="form-label text-dark fw-semibold">
                                {field.FieldLabel} {isRequired && <span className="text-danger">*</span>}
                              </label>
                              <select
                                className="form-select"
                                name="leaveReasonDropdown"
                                value={formData.leaveReasonDropdown}
                                onChange={handleInputChange}
                                required={isRequired}
                                disabled={isReadonly}
                              >
                                <option value="">Select Reason</option>
                                {dropdownBuckets.customer09.map((item) => (
                                  <option key={item.id || item.key} value={item.value}>
                                    {item.value}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        }

                        // CUSTOMER10: Day Type dropdown (Full Day, Forenoon, Afternoon)
                        if (fieldName === 'CUSTOMER10') {
                          return (
                            <div className="col-md-6 col-lg-4" key={index}>
                              <label className="form-label text-dark fw-semibold">
                                {field.FieldLabel} {isRequired && <span className="text-danger">*</span>}
                              </label>
                              <select
                                className="form-select"
                                name="dayType"
                                value={formData.dayType}
                                onChange={handleInputChange}
                                required={isRequired}
                                disabled={isReadonly}
                              >
                                <option value="">Select Day Type</option>
                                {dropdownBuckets.customer10.map((item) => (
                                  <option key={item.id || item.key} value={item.value}>
                                    {item.value}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        }

                        // CUSTOMER08: Checkbox (Sterilization of self)
                        if (fieldName === 'CUSTOMER08') {
                          return (
                            <div className="col-md-6 col-lg-4 d-flex align-items-center mt-4" key={index}>
                              <div className="form-check form-switch fs-14">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="sterilizationCheckbox"
                                  name="isSterilizationOfSelf"
                                  checked={formData.isSterilizationOfSelf}
                                  onChange={handleInputChange}
                                  disabled={isReadonly}
                                />
                                <label className="form-check-label fw-semibold text-dark ms-2" htmlFor="sterilizationCheckbox">
                                  {field.FieldLabel} {isRequired && <span className="text-danger">*</span>}
                                </label>
                              </div>
                            </div>
                          );
                        }

                        // Standard text inputs for known fields
                        let fieldVal = '';
                        let inputName = fieldName;
                        if (fieldName === 'CUSTOMER01') {
                          inputName = 'reasonForLeave1';
                          fieldVal = formData.reasonForLeave1;
                        } else if (fieldName === 'CUSTOMER03') {
                          inputName = 'reasonForLeave2';
                          fieldVal = formData.reasonForLeave2;
                        } else if (fieldName === 'CUSTOMER05') {
                          inputName = 'cityDistrict';
                          fieldVal = formData.cityDistrict;
                        } else if (fieldName === 'CUSTOMER06') {
                          inputName = 'hospitalizationStatus';
                          fieldVal = formData.hospitalizationStatus;
                        } else if (fieldName === 'CUSTOMER07') {
                          inputName = 'addressLine3';
                          fieldVal = formData.addressLine3;
                        } else {
                          fieldVal = dynamicFields[fieldName] || '';
                        }

                        return (
                          <div className="col-md-6 col-lg-4" key={index}>
                            <label className="form-label text-dark fw-semibold">
                              {field.FieldLabel} {isRequired && <span className="text-danger">*</span>}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name={inputName}
                              value={fieldVal}
                              onChange={(e) => {
                                if (['CUSTOMER01', 'CUSTOMER03', 'CUSTOMER05', 'CUSTOMER06', 'CUSTOMER07'].includes(fieldName)) {
                                  handleInputChange(e);
                                } else {
                                  handleDynamicFieldChange(fieldName, e.target.value);
                                }
                              }}
                              placeholder={`Enter ${field.FieldLabel}`}
                              required={isRequired}
                              readOnly={isReadonly}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

            {/* Section 4: Attachments */}
            <div className="card shadow-sm border-0 mb-4 rounded-3">
              <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <h5 className="card-title fw-bold text-dark mb-0 d-flex align-items-center">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center me-2 text-white"
                    style={{ backgroundColor: '#A93226', width: '28px', height: '28px', fontSize: '14px' }}
                  >
                    4
                  </span>
                  Attachments
                  {selectedLeaveConfig?.AttachmentMandatory && (
                    <span className="badge bg-danger ms-2 fs-11">Mandatory</span>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn btn-sm text-white d-flex align-items-center"
                  style={{ backgroundColor: '#A93226' }}
                  onClick={handleOpenUploadModal}
                >
                  <i className="ti ti-plus me-1"></i> Add Attachment
                </button>
              </div>
              <div className="card-body p-4">
                {attachments.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0 border">
                      <thead className="table-light">
                        <tr>
                          <th>Document Name</th>
                          <th style={{ width: '100px' }}>Type</th>
                          <th style={{ width: '120px' }}>Size</th>
                          <th style={{ width: '140px' }} className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attachments.map((att) => (
                          <tr key={att.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="ti ti-file-text fs-20 text-danger me-2"></i>
                                <div>
                                  <div className="fw-semibold text-dark">{att.name}</div>
                                  <small className="text-muted">{att.fileName}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-secondary-subtle text-secondary text-uppercase fs-11">
                                {att.type || att.fileExtension}
                              </span>
                            </td>
                            <td className="text-muted fs-13">
                              {att.fileSize.includes('KB') || att.fileSize.includes('MB')
                                ? att.fileSize
                                : `${(Number(att.fileSize || 0) / 1024).toFixed(1)} KB`}
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                {att.url && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary py-1 px-2"
                                    onClick={() => viewLeaveAttachmentFile(att.url)}
                                    title="View"
                                  >
                                    <i className="ti ti-eye"></i>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger py-1 px-2"
                                  onClick={() => handleRemoveAttachment(att.id)}
                                  title="Delete"
                                >
                                  <i className="ti ti-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 border border-dashed rounded text-center text-muted">
                    <i className="ti ti-cloud-upload fs-32 text-secondary mb-2 d-block"></i>
                    <p className="mb-1 fw-medium text-dark">No files attached yet</p>
                    <small className="text-muted d-block mb-3">
                      Allowed file types: PDF, JPG, PNG, DOC (Max {selectedLeaveConfig?.AttachMaxSize || 5}MB)
                    </small>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleOpenUploadModal}
                    >
                      <i className="ti ti-paperclip me-1"></i> Choose File & Upload
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Section 5: Action Buttons (Cancel / Check / Send) */}
            <div className="card shadow-sm border-0 rounded-3 mb-5">
              <div className="card-body p-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 py-2"
                  onClick={() => navigate(routes.LeaveManagement.list, { state: overrideEmployeeID ? locationState : undefined })}
                  disabled={isChecking || isSubmitting}
                >
                  <i className="ti ti-arrow-left me-1"></i> Cancel
                </button>

                <div className="d-flex gap-3">
                  {/* Check Button (Validate only) */}
                  <button
                    type="button"
                    className="btn btn-outline-primary px-4 py-2 fw-semibold d-flex align-items-center"
                    onClick={handleCheck}
                    disabled={isChecking || isSubmitting}
                  >
                    {isChecking ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Checking...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check me-2 fs-18"></i> Check
                      </>
                    )}
                  </button>

                  {/* Send Button (Validate and Open Summary Review Modal) */}
                  <button
                    type="button"
                    className="btn text-white px-5 py-2 fw-semibold shadow-sm d-flex align-items-center"
                    style={{ backgroundColor: '#A93226', borderColor: '#A93226' }}
                    onClick={handleSend}
                    disabled={isChecking || isSubmitting}
                  >
                    {isChecking ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Validating...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-send me-2 fs-18"></i> Send
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attachment Upload Modal (matches mobile handleOpenAttachmentModal) */}
      {showAttachmentModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content shadow border-0 rounded-3">
              <div className="modal-header py-3 bg-light border-bottom">
                <h6 className="modal-title fw-bold text-dark mb-0 d-flex align-items-center">
                  <i className="ti ti-paperclip me-2 text-primary"></i> Attachment Upload
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAttachmentModal(false)}
                  disabled={isUploadingFile}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-3">
                  <label className="form-label text-dark fw-semibold">
                    File <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose File
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="d-none"
                      onChange={handleFilePicked}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                    <span className="text-muted fs-13 text-truncate" style={{ maxWidth: '280px' }}>
                      {selectedFileForUpload ? selectedFileForUpload.name : 'No file chosen'}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label text-dark fw-semibold">Document Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customAttachmentName}
                    onChange={(e) => setCustomAttachmentName(e.target.value)}
                    placeholder="Enter custom document title"
                  />
                  <small className="text-muted">Optional: give this document a recognizable name.</small>
                </div>
              </div>
              <div className="modal-footer bg-light py-2 px-3 border-top d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowAttachmentModal(false)}
                  disabled={isUploadingFile}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn text-white px-4"
                  style={{ backgroundColor: '#A93226' }}
                  onClick={handleUploadAttachmentFromModal}
                  disabled={isUploadingFile || !selectedFileForUpload}
                >
                  {isUploadingFile ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Summary Modal (Replicating mobile Popupsummery.tsx) */}
      <LeaveSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        onConfirm={handleConfirmLeave}
        isSubmitting={isSubmitting}
        data={{
          ...formData,
          leaveTypeCode: selectedLeaveConfig?.AbsenceTypeCode || '',
          formattedStartDate: formatDateDisplay(formData.startDate),
          formattedEndDate: formatDateDisplay(formData.endDate),
          formattedDischargeDate: formData.dischargeDate ? formatDateDisplay(formData.dischargeDate) : '',
          formattedExpectedDueDate: formData.expectedDueDate ? formatDateDisplay(formData.expectedDueDate) : '',
          showDischargeInAdditionalSummary: useDischargeDateForRmda,
          leaveDays: calculateLeaveDays(),
          additionalFieldsConfig: selectedLeaveConfig?.AdditionalFields?.results || [],
          validationData,
          attachments,
        }}
        alerts={getAlertsForLeaveType(formData.leaveType, calculateLeaveDays())}
      />
    </div>
  );
};

export default LeaveManagementCreate;
